import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  BASE_URL,
  News_API,
  ImageUpload_API,
  ImageDelete_API,
} from "./environment";
import { useAuth } from "../context/AuthContext";
import {
  FaTrash,
  FaPlus,
  FaFilter,
  FaSave,
  FaTimes,
  FaEdit,
} from "react-icons/fa";
import { GrDocumentText } from "react-icons/gr";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";
import ContentEditor from "../components/ContentEditor";
import "./JobModal.css";

// Component for managing news with get, add, delete, multiple delete, filter, and view functionality
export default function News() {
  const { userData } = useAuth();
  const [showContentEditor, setShowContentEditor] = useState(false);

  const [newsItems, setNewsItems] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    title: [],
    date: [],
    created_by: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // "view", "edit", "add"
  const [editNews, setEditNews] = useState({});
  const [newVideoLink, setNewVideoLink] = useState(""); // For adding new video links

  const columnDisplay = {
    id: "ID",
    title: "Title",
    date: "Date",
    created_by: "Created By",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = newsItems.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.title.length === 0 || filters.title.includes(item.title)) &&
      (filters.date.length === 0 || filters.date.includes(item.date)) &&
      (filters.created_by.length === 0 ||
        filters.created_by.includes(item.created_by))
    );
  };

  // Filter news items
  const filteredNews = newsItems.filter(applyFilters);

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const paginatedNews = filteredNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all news on component mount
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(`${News_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch news: ${response.statusText}`);
        }
        const data = await response.json();
        // Ensure video_links is an array
        const normalizedData = data.map((item) => ({
          ...item,
          video_links: Array.isArray(item.video_links) ? item.video_links : [],
        }));
        setNewsItems(normalizedData);
      } catch (error) {
        console.error("Error fetching news:", error);
        Swal.fire("Error", "Failed to fetch news. Please try again.", "error");
      }
    };
    fetchNews();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered news
  const handleSelectAll = () => {
    if (selectedRows.length === filteredNews.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredNews.map((item) => item.id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Function to open modal for viewing a news item
  const viewNews = (news) => {
    setSelectedNews(news);
    setEditNews({
      ...news,
      video_links: Array.isArray(news.video_links) ? news.video_links : [],
    });
    setModalMode("view");
    setShowModal(true);
  };

  // Function to start editing a news item
  const startEdit = () => {
    setModalMode("edit");
  };

  // Function to add a new news item
  const addNews = () => {
    setEditNews({
      title: "",
      date: "",
      created_by: userData.username,
      banner_image: "",
      content: "",
      statement: "",
      person_name: "",
      person_image: "",
      about_person: "",
      video_links: [],
    });
    setModalMode("add");
    setShowModal(true);
  };

  // Handle input changes in the modal
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditNews((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Generate image name for banner_image in format news_dd-mm-yy_{random4}
  const generateImageName = (extension) => {
    const date = new Date();
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let random4 = "";
    for (let i = 0; i < 4; i++) {
      random4 += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return `news_${dd}-${mm}-${yy}_${random4}.${extension}`;
  };

  // Generate image name for person_image in format news_dd-mm-yy_{random4}Pson
  const generatePersonImageName = (extension) => {
    const date = new Date();
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let random4 = "";
    for (let i = 0; i < 4; i++) {
      random4 += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    return `news_${dd}-${mm}-${yy}_${random4}Pson.${extension}`;
  };

  // Delete image
  const deleteImage = async (path) => {
    if (!path) return;
    try {
      const response = await fetch(
        `${ImageDelete_API}?path=${encodeURIComponent(path)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      Swal.fire("Error", "Failed to delete image. Please try again.", "error");
    }
  };

  // Handle image upload with renaming
  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validImageTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/jpg",
    ];
    if (!validImageTypes.includes(file.type)) {
      Swal.fire(
        "Error",
        "Please select a valid image file (jpg, jpeg, png, webp)",
        "error"
      );
      return;
    }

    const extension = file.name.split(".").pop();
    const newFileName =
      imageType === "banner_image"
        ? generateImageName(extension)
        : generatePersonImageName(extension);
    const renamedFile = new File([file], newFileName, { type: file.type });
    const formData = new FormData();
    formData.append("file", renamedFile);

    try {
      // Delete old image if in edit mode and there is an existing image
      if (modalMode === "edit" && editNews[imageType]) {
        await deleteImage(editNews[imageType]);
      }

      const response = await fetch(ImageUpload_API, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userData.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload image: ${errorText}`);
      }

      const result = await response.json();
      setEditNews((prev) => ({
        ...prev,
        [imageType]: result.path,
      }));
      Swal.fire(
        "Success",
        `${
          imageType === "banner_image" ? "Banner" : "Person"
        } image uploaded successfully`,
        "success"
      );
    } catch (error) {
      console.error(`Error uploading ${imageType}:`, error);
      Swal.fire(
        "Error",
        `Failed to upload ${
          imageType === "banner_image" ? "banner" : "person"
        } image. Please try again.`,
        "error"
      );
    }
  };

  // Handle adding video link to array
  const addVideoLink = () => {
    if (newVideoLink && newVideoLink.trim()) {
      setEditNews((prev) => ({
        ...prev,
        video_links: [...prev.video_links, newVideoLink.trim()],
      }));
      setNewVideoLink("");
    } else {
      Swal.fire("Error", "Please enter a valid video link", "error");
    }
  };

  // Handle removing video link from array
  const removeVideoLink = (index) => {
    setEditNews((prev) => ({
      ...prev,
      video_links: prev.video_links.filter((_, i) => i !== index),
    }));
  };

  // Delete a single news item
  const deleteNews = async (id) => {
    const news = newsItems.find((item) => item.id === id);
    if (!news) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        // Delete banner_image and person_image if they exist
        if (news.banner_image) {
          await deleteImage(news.banner_image);
        }
        if (news.person_image) {
          await deleteImage(news.person_image);
        }

        const response = await fetch(`${News_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete news");
        }

        setNewsItems(newsItems.filter((item) => item.id !== id));
        setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
        Swal.fire("Deleted!", "News has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting news:", error);
        Swal.fire("Error", "Failed to delete news. Please try again.", "error");
      }
    }
  };

  // Delete selected news items
  const deleteSelectedRows = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedRows.length} news item(s). This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete them!",
    });

    if (result.isConfirmed) {
      try {
        // Delete images for each selected news item
        for (const id of selectedRows) {
          const news = newsItems.find((item) => item.id === id);
          if (news) {
            if (news.banner_image) {
              await deleteImage(news.banner_image);
            }
            if (news.person_image) {
              await deleteImage(news.person_image);
            }
          }
        }

        // Delete news items
        const deletePromises = selectedRows.map((id) =>
          fetch(`${News_API}/delete/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${userData.access_token}`,
            },
          }).then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to delete news item ${id}`);
            }
            return id;
          })
        );

        await Promise.all(deletePromises);

        setNewsItems(
          newsItems.filter((item) => !selectedRows.includes(item.id))
        );
        setSelectedRows([]);
        Swal.fire(
          "Deleted!",
          `${selectedRows.length} news item(s) deleted successfully.`,
          "success"
        );
      } catch (error) {
        console.error("Error deleting selected news:", error);
        Swal.fire(
          "Error",
          "Failed to delete selected news. Please try again.",
          "error"
        );
      }
    }
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
    setEditNews({});
    setModalMode("view");
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditNews({
      ...selectedNews,
      video_links: Array.isArray(selectedNews.video_links)
        ? selectedNews.video_links
        : [],
    });
    setModalMode("view");
  };

  // Render filter popup
  const renderFilterPopup = (column) => {
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchInput.toLowerCase())
    );

    return (
      <div className="filter-popup">
        <input
          type="text"
          placeholder={`Search ${columnDisplay[column]}`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <div className="filter-options">
          {filteredValues.map((value) => (
            <label key={value}>
              <input
                type="checkbox"
                checked={tempSelected.includes(value)}
                onChange={() =>
                  setTempSelected((prev) =>
                    prev.includes(value)
                      ? prev.filter((v) => v !== value)
                      : [...prev, value]
                  )
                }
              />
              {value}
            </label>
          ))}
        </div>
        <div className="filter-buttons">
          <button onClick={applyFilter}>Apply</button>
          <button
            onClick={() => {
              setTempSelected([]);
              setShowFilterPopup(null);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Render read-only field for ID
  const renderReadOnlyField = (name, label) => {
    return (
      <tr>
        <td>{label}</td>
        <td>{editNews[name]}</td>
      </tr>
    );
  };

  // Render editable or viewable field
  const renderField = (name, label, type = "text") => {
    const isEdit = modalMode === "edit";
    const isAdd = modalMode === "add";

    return (
      <tr>
        <td>{label}</td>
        <td>
          {isEdit || isAdd ? (
            type === "textarea" ? (
              <textarea
                name={name}
                value={editNews[name] || ""}
                onChange={handleChange}
              />
            ) : (
              <input
                type={type}
                name={name}
                value={editNews[name] || ""}
                onChange={handleChange}
              />
            )
          ) : (
            <div>{editNews[name] || "N/A"}</div>
          )}
        </td>
      </tr>
    );
  };

  // Render created_by field (read-only)
  const renderCreatedBy = () => {
    return (
      <tr>
        <td>Created By</td>
        <td>
          <div>{editNews.created_by || "N/A"}</div>
        </td>
      </tr>
    );
  };

  // Render banner_image field
  const renderBannerImage = () => {
    const imageUrl = editNews.banner_image
      ? `${BASE_URL}${editNews.banner_image}`
      : "";
    const isEdit = modalMode === "edit";
    const isAdd = modalMode === "add";

    return (
      <tr>
        <td>Banner Image</td>
        <td>
          {isEdit || isAdd ? (
            <div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={(e) => handleImageUpload(e, "banner_image")}
              />
              {editNews.banner_image && (
                <div>
                  <p>Uploaded: {editNews.banner_image}</p>
                  <img
                    src={imageUrl}
                    alt="Banner Preview"
                    style={{
                      maxWidth: "600px",
                      maxHeight: "300px",
                      marginTop: "10px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      console.error("Image Load Error:", {
                        url: imageUrl,
                        status: e.target.error?.message,
                      });
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                    onLoad={() =>
                      console.log("Image Loaded Successfully:", imageUrl)
                    }
                  />
                  <div style={{ display: "none", color: "red" }}>
                    Unable to load image
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div>{editNews.banner_image || "N/A"}</div>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Banner"
                  style={{
                    maxWidth: "600px",
                    maxHeight: "300px",
                    marginTop: "10px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.error("Image Load Error:", {
                      url: imageUrl,
                      status: e.target.error?.message,
                    });
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                  onLoad={() =>
                    console.log("Image Loaded Successfully:", imageUrl)
                  }
                />
              )}
              <div style={{ display: "none", color: "red" }}>
                Unable to load image
              </div>
            </div>
          )}
        </td>
      </tr>
    );
  };

  // Render person_image field
  const renderPersonImage = () => {
    const imageUrl = editNews.person_image
      ? `${BASE_URL}${editNews.person_image}`
      : "";
    const isEdit = modalMode === "edit";
    const isAdd = modalMode === "add";

    return (
      <tr>
        <td>Person Image</td>
        <td>
          {isEdit || isAdd ? (
            <div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={(e) => handleImageUpload(e, "person_image")}
              />
              {editNews.person_image && (
                <div>
                  <p>Uploaded: {editNews.person_image}</p>
                  <img
                    src={imageUrl}
                    alt="Person Preview"
                    style={{
                      maxWidth: "600px",
                      maxHeight: "300px",
                      marginTop: "10px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      console.error("Image Load Error:", {
                        url: imageUrl,
                        status: e.target.error?.message,
                      });
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                    onLoad={() =>
                      console.log("Image Loaded Successfully:", imageUrl)
                    }
                  />
                  <div style={{ display: "none", color: "red" }}>
                    Unable to load image
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div>{editNews.person_image || "N/A"}</div>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Person"
                  style={{
                    maxWidth: "600px",
                    maxHeight: "300px",
                    marginTop: "10px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.error("Image Load Error:", {
                      url: imageUrl,
                      status: e.target.error?.message,
                    });
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                  onLoad={() =>
                    console.log("Image Loaded Successfully:", imageUrl)
                  }
                />
              )}
              <div style={{ display: "none", color: "red" }}>
                Unable to load image
              </div>
            </div>
          )}
        </td>
      </tr>
    );
  };

  // Render video links array field
  const renderVideoLinks = () => {
    return (
      <tr>
        <td>Video Links</td>
        <td>
          {modalMode === "edit" || modalMode === "add" ? (
            <div>
              <ul>
                {editNews.video_links && editNews.video_links.length > 0 ? (
                  editNews.video_links.map((link, index) => (
                    <li key={index}>
                      {link}
                      <button
                        onClick={() => removeVideoLink(index)}
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </li>
                  ))
                ) : (
                  <li>No video links added</li>
                )}
              </ul>
              <input
                type="text"
                value={newVideoLink}
                onChange={(e) => setNewVideoLink(e.target.value)}
                placeholder="Enter video link"
              />
              <button onClick={addVideoLink} title="Add Video Link">
                <FaPlus />
              </button>
            </div>
          ) : (
            <ul>
              {editNews.video_links && editNews.video_links.length > 0 ? (
                editNews.video_links.map((link, index) => (
                  <li key={index}>{link}</li>
                ))
              ) : (
                <li>No video links</li>
              )}
            </ul>
          )}
        </td>
      </tr>
    );
  };

  // Save news (add or edit)
  const save = async () => {
    if (!editNews.title || !editNews.created_by || !editNews.content) {
      Swal.fire(
        "Error",
        "Title, Created By, and Content are required",
        "error"
      );
      return;
    }

    try {
      const url =
        modalMode === "add"
          ? `${News_API}/create`
          : `${News_API}/update/${editNews.id}`;
      const method = modalMode === "add" ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.access_token}`,
        },
        body: JSON.stringify({
          ...editNews,
          banner_image: editNews.banner_image || null,
          person_image: editNews.person_image || null,
          date: editNews.date || null,
          statement: editNews.statement || null,
          person_name: editNews.person_name || null,
          about_person: editNews.about_person || null,
          video_links: editNews.video_links || [],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to ${modalMode === "add" ? "create" : "update"} news`
        );
      }

      const updatedNews = await response.json();
      if (modalMode === "add") {
        setNewsItems([...newsItems, updatedNews]);
      } else {
        setNewsItems(
          newsItems.map((item) =>
            item.id === updatedNews.id ? updatedNews : item
          )
        );
      }

      Swal.fire(
        "Success!",
        `News ${modalMode === "add" ? "created" : "updated"} successfully`,
        "success"
      );
      setShowModal(false);
    } catch (error) {
      console.error(
        `Error ${modalMode === "add" ? "creating" : "updating"} news:`,
        error
      );
      Swal.fire(
        "Error",
        `Failed to ${
          modalMode === "add" ? "create" : "update"
        } news. Please try again.`,
        "error"
      );
    }
  };

  // Render modal for view/edit/add
  const renderModal = () => {
    if (!showModal) return null;
    const isEdit = modalMode === "edit";
    const isAdd = modalMode === "add";

    return (
      <div className="job-modal-overlay">
        <div className="job-modal">
          <div className="job-modal-header">
            <h2>
              {isAdd ? "Add New News" : `(${editNews.id}) ${editNews.title}`}
            </h2>
            <div className="job-modal-actions">
              {!isEdit && !isAdd && (
                <button onClick={startEdit}>
                  <FaEdit />
                </button>
              )}
              {(isEdit || isAdd) && (
                <>
                  <button onClick={save}>
                    <FaSave />
                  </button>
                  <button onClick={isEdit ? cancelEdit : closeModal}>
                    <FaTimes />
                  </button>
                </>
              )}
              <button className="job-modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
          </div>
          <table className="job-details-table">
            <tbody>
              {!isAdd && renderReadOnlyField("id", "ID")}
              {renderField("title", "Title")}
              {renderField("date", "Date", "date")}
              {renderCreatedBy()}
              {renderBannerImage()}
              <tr>
                <td>Content</td>
                <td>
                  {modalMode === "edit" || modalMode === "add" ? (
                    <>
                      <button
                        onClick={() => setShowContentEditor(true)}
                        className="btn"
                      >
                        Add Content
                      </button>

                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          border: "1px solid var(--border)",
                        }}
                        dangerouslySetInnerHTML={{ __html: editNews.content }}
                      />
                    </>
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: editNews.content }}
                    />
                  )}
                </td>
              </tr>

              {renderField("statement", "Statement", "textarea")}
              {renderField("person_name", "Person Name")}
              {renderPersonImage()}
              {renderField("about_person", "About Person", "textarea")}
              {renderVideoLinks()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>News - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>News</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredNews.length &&
                    filteredNews.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredNews.length &&
                      filteredNews.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                ID
                <FaFilter
                  className={`filter-icon ${
                    filters.id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>
                Title
                <FaFilter
                  className={`filter-icon ${
                    filters.title.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("title")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "title" && renderFilterPopup("title")}
              </th>
              <th style={{ position: "relative" }}>
                Date
                <FaFilter
                  className={`filter-icon ${
                    filters.date.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("date")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "date" && renderFilterPopup("date")}
              </th>
              <th style={{ position: "relative" }}>
                Created By
                <FaFilter
                  className={`filter-icon ${
                    filters.created_by.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("created_by")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "created_by" &&
                  renderFilterPopup("created_by")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedNews.map((news) => (
              <tr
                key={news.id}
                className={selectedRows.includes(news.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(news.id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(news.id)}
                    onChange={() => toggleRowSelection(news.id)}
                  />
                </td>
                <td>{news.id}</td>
                <td>{news.title}</td>
                <td>{news.date}</td>
                <td>{news.created_by}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => viewNews(news)}
                    disabled={selectedRows.length > 1}
                  >
                    <GrDocumentText />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteNews(news.id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addNews}>
        <FaPlus />
      </button>

      {renderModal()}
      {showContentEditor && (
        <ContentEditor
          initialContent={editNews.content}
          onSave={(html) => {
            setEditNews((prev) => ({
              ...prev,
              content: html,
            }));
            setShowContentEditor(false);
          }}
          onClose={() => setShowContentEditor(false)}
        />
      )}
    </div>
  );
}
